import { useState, useRef, useEffect } from 'react';
import { format, subDays, subMonths, startOfMonth, endOfMonth } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { DatePickerSingle } from '@/components/ui/DatePickerSingle';
import { filterTriggerHover } from './filterTriggerStyles';

/**
 * DateRangePicker Component
 *
 * Single field that opens a dropdown picker for selecting date ranges.
 * Includes presets and custom date range selection.
 */

interface DateRange {
  from: Date | null;
  to: Date | null;
}

interface DateRangePickerProps {
  value: DateRange;
  onChange: (range: DateRange) => void;
  placeholder?: string;
  className?: string;
  /** Open the dropdown above the trigger instead of below */
  openUpward?: boolean;
  size?: 'sm' | 'md';
}

interface Preset {
  label: string;
  getValue: () => DateRange;
}

const PRESETS: Preset[] = [
  {
    label: 'Today',
    getValue: () => ({ from: new Date(), to: new Date() }),
  },
  {
    label: 'Yesterday',
    getValue: () => ({
      from: subDays(new Date(), 1),
      to: subDays(new Date(), 1),
    }),
  },
  {
    label: 'Last 7 days',
    getValue: () => ({ from: subDays(new Date(), 7), to: new Date() }),
  },
  {
    label: 'Last 30 days',
    getValue: () => ({ from: subDays(new Date(), 30), to: new Date() }),
  },
  {
    label: 'This month',
    getValue: () => ({
      from: startOfMonth(new Date()),
      to: endOfMonth(new Date()),
    }),
  },
  {
    label: 'Last month',
    getValue: () => ({
      from: startOfMonth(subMonths(new Date(), 1)),
      to: endOfMonth(subMonths(new Date(), 1)),
    }),
  },
];

export const DateRangePicker = ({
  value,
  onChange,
  placeholder = 'Select period',
  className = '',
  openUpward = false,
  size = 'md',
}: DateRangePickerProps) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [shouldOpenUp, setShouldOpenUp] = useState(openUpward);
  const ref = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  // Auto-detect direction: open upward if not enough space below
  useEffect(() => {
    if (isOpen && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      // Dropdown is roughly 350px tall
      setShouldOpenUp(openUpward || spaceBelow < 350);
    }
  }, [isOpen, openUpward]);

  // Close on outside click (but not when clicking inside nested Radix popovers like DatePickerSingle)
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (ref.current && !ref.current.contains(target)) {
        // Don't close if clicking inside a Radix popover portal (e.g. DatePickerSingle calendar)
        const popoverContent = (target as Element).closest?.('[data-radix-popper-content-wrapper]');
        if (popoverContent) return;
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  /**
   * Format display value
   * Shows formatted date range or placeholder
   */
  const displayValue =
    value.from && value.to
      ? `${format(value.from, 'dd.MM.yyyy')} - ${format(value.to, 'dd.MM.yyyy')}`
      : placeholder;

  /**
   * Handle preset selection
   */
  const handlePresetClick = (preset: Preset) => {
    onChange(preset.getValue());
    setIsOpen(false);
  };

  /**
   * Handle custom date input change
   */
  const handleFromChange = (isoDate: string) => {
    const newFrom = isoDate ? new Date(isoDate) : null;
    onChange({ ...value, from: newFrom });
  };

  const handleToChange = (isoDate: string) => {
    const newTo = isoDate ? new Date(isoDate) : null;
    onChange({ ...value, to: newTo });
  };

  /**
   * Clear date range
   */
  const handleClear = () => {
    onChange({ from: null, to: null });
    setIsOpen(false);
  };

  /**
   * Format date as ISO string for DatePickerSingle
   */
  const formatForInput = (date: Date | null): string => {
    if (!date) return '';
    try {
      return date.toISOString();
    } catch {
      return '';
    }
  };

  return (
    <div className={`relative ${className}`} ref={ref}>
      {/* Trigger Button */}
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`
          w-full ${size === 'sm' ? 'px-3 h-[34px] text-xs lg:h-auto lg:px-4 lg:py-3 lg:text-sm' : 'px-4 py-3 text-sm'}
          text-left
          border border-gray-200 rounded-lg
          bg-white
          ${filterTriggerHover}
          focus:outline-none focus:border-gray-400
          transition-colors duration-200
          flex items-center justify-between
          ${value.from ? 'text-gray-900' : 'text-gray-400'}
        `}
      >
        <span>{displayValue}</span>
        <svg
          className="w-5 h-5 text-gray-400"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
          />
        </svg>
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className={`absolute z-50 w-80 max-w-[calc(100vw-2rem)] bg-white border border-gray-200 rounded-lg shadow-lg ${shouldOpenUp ? 'bottom-full mb-1' : 'mt-1'}`}>
          {/* Presets */}
          <div className="p-3 border-b border-gray-100">
            <p className="text-xs font-medium text-gray-500 mb-2">{t('date_range_quick_select')}</p>
            <div className="grid grid-cols-2 gap-2">
              {PRESETS.map((preset) => (
                <button
                  key={preset.label}
                  type="button"
                  onClick={() => handlePresetClick(preset)}
                  className="px-3 py-2 text-xs text-left text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
                >
                  {preset.label}
                </button>
              ))}
            </div>
          </div>

          {/* Custom Date Inputs */}
          <div className="p-3 space-y-3 border-b border-gray-100">
            <p className="text-xs font-medium text-gray-500 mb-2">{t('date_range_custom_range')}</p>
            <DatePickerSingle
              label={t('date_range_from')}
              value={formatForInput(value.from)}
              onChange={handleFromChange}
              maxDate={value.to || undefined}
              placeholder="dd.MM.yyyy"
            />
            <DatePickerSingle
              label={t('date_range_to')}
              value={formatForInput(value.to)}
              onChange={handleToChange}
              minDate={value.from || undefined}
              placeholder="dd.MM.yyyy"
            />
          </div>

          {/* Clear Button */}
          <div className="p-2">
            <button
              type="button"
              onClick={handleClear}
              className="w-full px-3 py-2 text-xs text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors"
            >
              {t('clear_dates')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
